/* eslint-disable jsx-a11y/no-static-element-interactions */
import ShareSocialButton from "@components/Blocks/ShareSocial/ShareSocialButton";
import { QnHandGesture } from "@components/Layouts/QuickNews/QnButtons";
import {
  CAROUSEL_TRANSITION_DURATION,
  CAROUSEL_TRANSITION_EASING,
  QN_ANALYTICS_EVENT_BASE,
  QN_SOCIAL_ICON_LIST,
} from "@config/QuickNewsConstants";
import { useTMCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useTMCustomEventAnalyticsCall";
import React, { memo, useCallback, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

import { CarouselProps } from "./QnPhotoGalleryCard";
import { useCarouselDrag } from "./useCarouselDrag";

const OPACITY_TRANSITION = "opacity 0.3s ease";

const QnTmPicksCard = memo(
  ({
    item,
    isActive,
    index,
    setActiveTab,
    onPrev,
    onNext,
    typeIndex,
  }: CarouselProps) => {
    const sendAnalyticsEvent = useTMCustomEventAnalyticsCall();
    const images = item.imageOriginalGallery || [];
    const [current, setCurrent] = useState(0);

    const {
      isDragging,
      currentTranslate,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd,
      handleMouseDown,
      handleMouseMove,
      handleMouseUp,
      nextSlide,
      prevSlide,
    } = useCarouselDrag({
      current,
      imagesLength: images.length,
      index,
      itemTitle: item.title,
      setActiveTab,
      onPrev,
      onNext,
      analyticsPrefix: "carousel tmpicks",
      resetTranslateOnStart: true,
      onSetCurrent: (newCurrent) => {
        setCurrent(newCurrent);
      },
    });

    const onTouchMove = useCallback(
      (e: React.TouchEvent | React.MouseEvent) => {
        handleTouchMove(e);
      },
      [handleTouchMove],
    );

    const onTouchEnd = useCallback(
      (e: React.TouchEvent | React.MouseEvent) => {
        handleTouchEnd(e);
      },
      [handleTouchEnd],
    );

    const slidesTransform = useMemo(
      () => ({
        transform: `translateX(calc(-${current * 100}% + ${currentTranslate.current}px))`,
        transition: isDragging
          ? "none"
          : `transform ${CAROUSEL_TRANSITION_DURATION} ${CAROUSEL_TRANSITION_EASING}`,
      }),
      [current, isDragging, currentTranslate],
    );

    const imageStyle = useCallback(
      (i: number) => ({
        opacity: i === current ? 1 : 0,
        transition: isDragging ? "none" : OPACITY_TRANSITION,
      }),
      [current, isDragging],
    );

    return (
      <div className="h-full w-full flex flex-col justify-center m-auto items-start bg-black-100/5">
        <h1 className="relative flex items-start px-2.5 font-primary text-md font-semibold leading-[150%] text-orange-500">
          முரசு மேடை
        </h1>

        <div className="relative mx-auto flex h-auto items-center justify-center p-2.5 md:h-[445px]">
          <div
            key={item.id}
            className="gallery-wrapper relative mx-auto h-auto w-full pb-2.5 md:h-[430px] md:w-[430px]"
            style={{ overflow: isDragging ? "visible" : "hidden" }}
            onTouchStart={handleTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          >
            {isActive && <QnHandGesture canvasId="qn-tm-picks-hand-gesture" />}

            {/* Slides */}
            <div className="flex h-full" style={slidesTransform}>
              {images.map((img, i) => (
                <img
                  key={i}
                  src={img.image?.url}
                  alt={img.caption || item.title || "image"}
                  style={imageStyle(i)}
                  className={twMerge(
                    "w-full flex-shrink-0 rounded-2xl px-1 shadow-8xl",
                  )}
                  loading="lazy"
                  decoding="async"
                />
              ))}
            </div>

            {/* Prev / Next */}
            {images.length > 1 &&
              ["left", "right"].map((direction) => (
                <button
                  key={`tm-pick-${direction}`}
                  onClick={direction === "left" ? prevSlide : nextSlide}
                  className={`absolute ${direction}-0 top-0 z-10 h-full w-[50px] px-2.5`}
                />
              ))}
          </div>

          {/* Dots */}
          {images.length > 1 && (
            <div className="absolute bottom-0 flex w-full justify-center gap-2">
              {images.map((_, key) => (
                <button
                  key={key}
                  onClick={() => {
                    setCurrent(key);
                  }}
                  className={twMerge(
                    "h-xxs w-xxs rounded-full bg-white-100 transition-[width] duration-300",
                    key === current && "!w-3",
                  )}
                />
              ))}
            </div>
          )}
        </div>

        <div className="absolute bottom-1 right-1 pb-5">
          <ShareSocialButton
            socialIconList={QN_SOCIAL_ICON_LIST}
            pagePath={`/quick-news/${item.id}`}
            mbPopupClassName="absolute right-0 -top-14"
            mbClassName="flex justify-end px-4"
            quickNews
            quickNewsLight
            onClickGaEvent={() => {
              sendAnalyticsEvent({
                ...QN_ANALYTICS_EVENT_BASE,
                eventAction: `share::${typeIndex + 1}`,
                eventLabel: item.title,
              });
            }}
          />
        </div>
      </div>
    );
  },
);

QnTmPicksCard.displayName = "QnTmPicksCard";

export default QnTmPicksCard;
